import { formatDateTimeDDMMYY } from '../../utils/dateFormat';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import PaidIcon from '@mui/icons-material/Paid';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { useNavigate } from 'react-router-dom';

import { FinancialReportPeriod, FinancialReportsSummary, getFinancialReports } from '../../services/adminService';
import { useSnackbar } from '../../contexts/SnackbarContext';

type ChartMode = 'revenue' | 'risk' | 'count';

const ADMIN_LEDGER_CURRENCY_KEY = 'streamy.admin.displayCurrency';
const periods: FinancialReportPeriod[] = ['daily', 'weekly', 'monthly', 'quarterly'];

const cardSx = {
  borderRadius: 0,
  border: '0.5px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  overflow: 'hidden',
} as const;

const labelSx = {
  fontSize: '0.64rem',
  fontWeight: 900,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
  color: 'text.disabled',
};

const monoSx = {
  fontFamily: "'DM Mono', monospace",
  fontSize: '0.76rem',
  fontWeight: 800,
};

const formatMoney = (currency: string, amount?: number) =>
  `${currency} ${Number(amount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const formatDate = (value?: string | null) => {
  return formatDateTimeDDMMYY(value);
};

const csvEscape = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const downloadBlob = (filename: string, content: string, type = 'text/csv;charset=utf-8') => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const Metric = ({ label, value, helper, color, icon }: { label: string; value: React.ReactNode; helper?: string; color: string; icon: React.ReactNode }) => (
  <Paper elevation={0} sx={{ ...cardSx, p: 1.35, minHeight: 104 }}>
    <Stack direction="row" spacing={1.1} alignItems="flex-start">
      <Box sx={{ width: 34, height: 34, border: '0.5px solid', borderColor: color, bgcolor: alpha(color, 0.08), color, display: 'grid', placeItems: 'center' }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={labelSx}>{label}</Typography>
        <Typography sx={{ fontSize: '1.24rem', fontWeight: 950, lineHeight: 1.12 }}>{value}</Typography>
        {helper && <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', mt: 0.25 }}>{helper}</Typography>}
      </Box>
    </Stack>
  </Paper>
);

const MoneyBreakdown = ({ title, rows, currency, color }: { title: string; rows: Array<{ name: string; amount: number; count: number }>; currency: string; color: string }) => {
  const total = rows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const visibleRows = rows.length ? rows.slice(0, 8) : [{ name: 'No data', amount: 0, count: 0 }];
  return (
    <Paper elevation={0} sx={{ ...cardSx }}>
      <Box sx={{ px: 1.5, py: 1, borderBottom: '0.5px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Typography sx={{ fontSize: '0.84rem', fontWeight: 950 }}>{title}</Typography>
        <Typography sx={monoSx}>{formatMoney(currency, total)}</Typography>
      </Box>
      <Stack spacing={1} sx={{ p: 1.25 }}>
        {visibleRows.map((row) => {
          const pct = total > 0 ? Math.min(100, (Number(row.amount || 0) / total) * 100) : 0;
          return (
            <Box key={`${title}-${row.name}`}>
              <Stack direction="row" spacing={1} justifyContent="space-between">
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 850 }} noWrap>{row.name}</Typography>
                <Typography sx={monoSx}>{formatMoney(currency, row.amount)} / {row.count}</Typography>
              </Stack>
              <LinearProgress variant="determinate" value={pct} sx={{ mt: 0.55, height: 3, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { bgcolor: color } }} />
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

const CountBreakdown = ({ title, rows, color }: { title: string; rows: Array<{ name: string; value: number }>; color: string }) => {
  const total = rows.reduce((sum, row) => sum + Number(row.value || 0), 0);
  const visibleRows = rows.length ? rows.slice(0, 8) : [{ name: 'No data', value: 0 }];
  return (
    <Paper elevation={0} sx={{ ...cardSx }}>
      <Box sx={{ px: 1.5, py: 1, borderBottom: '0.5px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Typography sx={{ fontSize: '0.84rem', fontWeight: 950 }}>{title}</Typography>
        <Typography sx={monoSx}>{total}</Typography>
      </Box>
      <Stack spacing={1} sx={{ p: 1.25 }}>
        {visibleRows.map((row) => {
          const pct = total > 0 ? Math.min(100, (Number(row.value || 0) / total) * 100) : 0;
          return (
            <Box key={`${title}-${row.name}`}>
              <Stack direction="row" spacing={1} justifyContent="space-between">
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 850 }} noWrap>{row.name}</Typography>
                <Typography sx={monoSx}>{row.value}</Typography>
              </Stack>
              <LinearProgress variant="determinate" value={pct} sx={{ mt: 0.55, height: 3, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { bgcolor: color } }} />
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

const reportToCsv = (report: FinancialReportsSummary) => {
  const rows: string[][] = [
    ['Section', 'Metric', 'Value', 'Count', 'Currency'],
    ['Summary', 'Collected', String(report.totals.collected), String(report.totals.paidCount), report.currency],
    ['Summary', 'Pending', String(report.totals.pending), String(report.totals.pendingCount), report.currency],
    ['Summary', 'Failed', String(report.totals.failed), String(report.totals.failedCount), report.currency],
    ['Summary', 'Receivables', String(report.totals.receivables), String(report.totals.invoiceCount), report.currency],
    ['Summary', 'Collection Rate', `${report.totals.collectionRate}%`, '', report.currency],
    ['Summary', 'Failure Rate', `${report.totals.failureRate}%`, '', report.currency],
    ['Summary', 'ARPU', String(report.totals.arpu), String(report.totals.uniquePaidUsers), report.currency],
    ...report.breakdowns.incomeStreams.map((row) => ['Income Stream', row.name, String(row.amount), String(row.count), report.currency]),
    ...report.breakdowns.resources.map((row) => ['Resource', row.name, String(row.amount), String(row.count), report.currency]),
    ...report.breakdowns.gateways.map((row) => ['Gateway', row.name, String(row.amount), String(row.count), report.currency]),
    ...report.breakdowns.statuses.map((row) => ['Status', row.name, String(row.value), '', report.currency]),
    ...report.breakdowns.invoices.map((row) => ['Invoice', row.name, String(row.value), '', report.currency]),
    ...report.topCustomers.map((row) => ['Top Customer', row.userEmail, String(row.amount), String(row.count), report.currency]),
    ...report.history.map((row) => ['Trend', row.label, `collected=${row.collected}; pending=${row.pending}; failed=${row.failed}`, String(row.count), report.currency]),
  ];
  return rows.map((row) => row.map(csvEscape).join(',')).join('\n');
};

export const FinancialReportsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [period, setPeriod] = useState<FinancialReportPeriod>('monthly');
  const [currency, setCurrency] = useState(() => localStorage.getItem(ADMIN_LEDGER_CURRENCY_KEY) || 'USD');
  const [chartMode, setChartMode] = useState<ChartMode>('revenue');
  const [report, setReport] = useState<FinancialReportsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<FinancialReportPeriod | null>(null);

  const load = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setReport(await getFinancialReports(period, currency));
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to load financial reports', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = (event: Event) => {
      const next = (event as CustomEvent<{ currency?: string }>).detail?.currency;
      if (next) setCurrency(next);
    };
    window.addEventListener('streamy-admin-currency-change', handler);
    return () => window.removeEventListener('streamy-admin-currency-change', handler);
  }, []);

  useEffect(() => {
    load();
    const interval = window.setInterval(() => load(true), 30000);
    return () => window.clearInterval(interval);
  }, [period, currency]);

  const downloadPeriod = async (targetPeriod: FinancialReportPeriod) => {
    setDownloading(targetPeriod);
    try {
      const snapshot = targetPeriod === period && report ? report : await getFinancialReports(targetPeriod, currency);
      const filename = `streamy-financial-${targetPeriod}-${new Date().toISOString().slice(0, 10)}.csv`;
      downloadBlob(filename, reportToCsv(snapshot));
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to download financial report', 'error');
    } finally {
      setDownloading(null);
    }
  };

  const chartData = report?.history || [];
  const collectionTone = Number(report?.totals.collectionRate || 0) >= 90 ? theme.palette.success.main : theme.palette.warning.main;
  const failureTone = Number(report?.totals.failureRate || 0) > 5 ? theme.palette.error.main : theme.palette.success.main;

  if (loading && !report) {
    return <Box sx={{ p: 8, display: 'flex', justifyContent: 'center' }}><CircularProgress size={28} /></Box>;
  }

  return (
    <Box sx={{ maxWidth: 1780, mx: 'auto' }}>
      <Paper elevation={0} sx={{ ...cardSx, p: 1.5, mb: 1.5 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
          <Stack direction="row" spacing={1.1} alignItems="center">
            <IconButton size="small" onClick={() => navigate('/admin/billing')} sx={{ borderRadius: 0, border: '0.5px solid', borderColor: 'divider' }}><ArrowBackIcon sx={{ fontSize: 18 }} /></IconButton>
            <ReceiptLongIcon sx={{ color: 'text.secondary' }} />
            <Box>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 950 }}>Financial Reports</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Strategic revenue, receivables, gateway, resource, and risk metrics.</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent="flex-end">
            {periods.map((item) => (
              <Chip key={item} label={item} onClick={() => setPeriod(item)} color={period === item ? 'primary' : 'default'} variant={period === item ? 'filled' : 'outlined'} sx={{ borderRadius: 0, fontWeight: 900, textTransform: 'capitalize' }} />
            ))}
            <Tooltip title="Refresh financial report">
              <span>
                <IconButton size="small" onClick={() => load()} disabled={loading} sx={{ borderRadius: 0, border: '0.5px solid', borderColor: 'divider', width: 34, height: 34 }}>
                  {loading ? <CircularProgress size={16} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {!report ? <Alert severity="warning" sx={{ borderRadius: 0 }}>No financial report data available.</Alert> : (
        <Stack spacing={1.5}>
          <Paper elevation={0} sx={{ ...cardSx, p: 1.25 }}>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
              <Box>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 900 }}>Report Window</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{formatDate(report.range.start)} - {formatDate(report.range.end)} / Generated {formatDate(report.generatedAt)}</Typography>
              </Box>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {periods.map((item) => (
                  <Button key={item} size="small" variant={period === item ? 'contained' : 'outlined'} startIcon={downloading === item ? <CircularProgress size={14} /> : <DownloadIcon />} disabled={!!downloading} onClick={() => downloadPeriod(item)} sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 900 }}>
                    {item}
                  </Button>
                ))}
              </Stack>
            </Stack>
          </Paper>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }, gap: 1.25 }}>
            <Metric label="Collected" value={formatMoney(report.currency, report.totals.collected)} helper={`${report.totals.paidCount} paid / ${report.totals.uniquePaidUsers} payers`} icon={<PaidIcon fontSize="small" />} color={theme.palette.success.main} />
            <Metric label="Collection Rate" value={`${report.totals.collectionRate}%`} helper={`${formatMoney(report.currency, report.totals.attempted)} attempted`} icon={<TrendingUpIcon fontSize="small" />} color={collectionTone} />
            <Metric label="Receivables" value={formatMoney(report.currency, report.totals.receivables)} helper={`${report.totals.invoiceCount} invoices in window`} icon={<ReceiptLongIcon fontSize="small" />} color={theme.palette.info.main} />
            <Metric label="Failure Rate" value={`${report.totals.failureRate}%`} helper={`${report.totals.failedCount} failed / ${formatMoney(report.currency, report.totals.failed)}`} icon={<WarningAmberIcon fontSize="small" />} color={failureTone} />
            <Metric label="ARPU" value={formatMoney(report.currency, report.totals.arpu)} helper="paid users in window" icon={<PaidIcon fontSize="small" />} color={theme.palette.secondary.main} />
            <Metric label="Avg Transaction" value={formatMoney(report.currency, report.totals.avgTransaction)} helper={`${report.totals.paidCount} paid payments`} icon={<ReceiptLongIcon fontSize="small" />} color={theme.palette.primary.main} />
            <Metric label="Rev / Channel" value={formatMoney(report.currency, report.totals.revenuePerChannel)} helper={`${report.totals.activeChannels} active channels`} icon={<TrendingUpIcon fontSize="small" />} color={theme.palette.info.main} />
            <Metric label="Rev / Output" value={formatMoney(report.currency, report.totals.revenuePerOutput)} helper={`${report.totals.activeOutputs} active outputs`} icon={<TrendingUpIcon fontSize="small" />} color={theme.palette.info.main} />
            <Metric label="Pending" value={formatMoney(report.currency, report.totals.pending)} helper={`${report.totals.pendingCount} pending payments`} icon={<WarningAmberIcon fontSize="small" />} color={theme.palette.warning.main} />
            <Metric label="Refunded" value={formatMoney(report.currency, report.totals.refunded)} helper={`${report.totals.refundedCount} refund records`} icon={<ReceiptLongIcon fontSize="small" />} color={theme.palette.text.secondary} />
            <Metric label="Scheduled Loss" value={formatMoney(report.currency, report.cancellationImpact?.totals.monthlyLostRevenue || 0)} helper={`${report.cancellationImpact?.totals.count || 0} scheduled removals`} icon={<WarningAmberIcon fontSize="small" />} color={theme.palette.error.main} />
            <Metric label="Active Estate" value={`${report.totals.activeUsers} / ${report.totals.activeChannels}`} helper="active users / channels" icon={<TrendingUpIcon fontSize="small" />} color={theme.palette.success.main} />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.7fr) minmax(360px, 0.9fr)' }, gap: 1.5 }}>
            <Paper elevation={0} sx={{ ...cardSx, p: 1.5 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 1.2 }}>
                <Box>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 950 }}>Financial Trend</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Period-scoped revenue, payment risk, and transaction volume.</Typography>
                </Box>
                <Stack direction="row" spacing={0.65} flexWrap="wrap" useFlexGap>
                  {(['revenue', 'risk', 'count'] as ChartMode[]).map((item) => (
                    <Chip key={item} label={item} onClick={() => setChartMode(item)} color={chartMode === item ? 'secondary' : 'default'} variant={chartMode === item ? 'filled' : 'outlined'} sx={{ borderRadius: 0, fontWeight: 900, textTransform: 'capitalize' }} />
                  ))}
                </Stack>
              </Stack>
              <Box sx={{ height: 310, minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                  {chartMode === 'count' ? (
                    <BarChart data={chartData} margin={{ top: 10, right: 16, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <ChartTooltip />
                      <Bar dataKey="count" name="Transactions" fill={theme.palette.secondary.main} />
                    </BarChart>
                  ) : (
                    <LineChart data={chartData} margin={{ top: 10, right: 16, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={24} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <ChartTooltip />
                      {chartMode === 'revenue' && <Line type="monotone" dataKey="collected" name="Collected" stroke={theme.palette.success.main} dot={false} strokeWidth={2} />}
                      {chartMode === 'risk' && <Line type="monotone" dataKey="pending" name="Pending" stroke={theme.palette.warning.main} dot={false} strokeWidth={2} />}
                      {chartMode === 'risk' && <Line type="monotone" dataKey="failed" name="Failed" stroke={theme.palette.error.main} dot={false} strokeWidth={2} />}
                    </LineChart>
                  )}
                </ResponsiveContainer>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ ...cardSx }}>
              <Box sx={{ px: 1.5, py: 1, borderBottom: '0.5px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '0.86rem', fontWeight: 950 }}>Top Customers</Typography>
                <Typography sx={monoSx}>{report.topCustomers.length}</Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 310 }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow><TableCell sx={labelSx}>Customer</TableCell><TableCell align="right" sx={labelSx}>Revenue</TableCell><TableCell align="right" sx={labelSx}>Payments</TableCell></TableRow></TableHead>
                  <TableBody>
                    {(report.topCustomers.length ? report.topCustomers : [{ userEmail: 'No paid customers in window', amount: 0, count: 0 }]).map((row) => (
                      <TableRow key={`${row.userEmail}-${row.userHid || ''}`} hover>
                        <TableCell><Typography sx={{ fontSize: '0.76rem', fontWeight: 900 }}>{row.userEmail}</Typography><Typography sx={{ ...monoSx, color: 'text.disabled' }}>{row.userHid || '--'}</Typography></TableCell>
                        <TableCell align="right" sx={monoSx}>{formatMoney(report.currency, row.amount)}</TableCell>
                        <TableCell align="right" sx={monoSx}>{row.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(5, minmax(0, 1fr))' }, gap: 1.25 }}>
            <MoneyBreakdown title="Income Streams" rows={report.breakdowns.incomeStreams} currency={report.currency} color={theme.palette.success.main} />
            <MoneyBreakdown title="Resources" rows={report.breakdowns.resources} currency={report.currency} color={theme.palette.primary.main} />
            <MoneyBreakdown title="Gateways" rows={report.breakdowns.gateways} currency={report.currency} color={theme.palette.info.main} />
            <CountBreakdown title="Payment Status" rows={report.breakdowns.statuses} color={theme.palette.warning.main} />
            <CountBreakdown title="Invoice State" rows={report.breakdowns.invoices} color={theme.palette.secondary.main} />
          </Box>
        </Stack>
      )}
    </Box>
  );
};
